/**
 * Weight Loader Utility
 * 
 * Provides runtime weight override loading from database,
 * with localStorage caching for synchronous access in mapping functions.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/structuredLogging';

/**
 * Legacy → canonical trait-ID normalizer.
 *
 * Persisted overrides in `scoring_weight_overrides` may still use bare names
 * (`Independent`, `Social`). Rewrite the weights map on read so downstream
 * consumers see the canonical `domain.triad.position` IDs from
 * `src/scoring/traitIds.ts`. Warn once (dev only) so we can measure how often
 * legacy names still appear in the wild before the A2 production-data audit.
 */
const LEGACY_BARE_TO_CANONICAL: Record<string, string> = {
  Independent: 'interpersonal.navigate.independent',
  Social: 'processing.reality.social',
};
const legacyWarned = new Set<string>();
function normalizeWeights(weights: Record<string, number>): Record<string, number> {
  let touched = false;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(weights)) {
    const canonical = LEGACY_BARE_TO_CANONICAL[k];
    if (canonical) {
      out[canonical] = v;
      if (import.meta.env?.DEV && !legacyWarned.has(k)) {
        legacyWarned.add(k);
        // eslint-disable-next-line no-console
        console.warn(`[weightLoader] legacy bare name "${k}" normalized → "${canonical}" (A2 audit pending)`);
      }
      touched = true;
    } else {
      out[k] = v;
    }
  }
  return touched ? out : weights;
}

const WEIGHT_CACHE_KEY = 'tps_weight_overrides';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
/** Version of the production weight set the cached overrides came from. */
const WEIGHT_VERSION_KEY = 'tps_weight_overrides_version';
const VERSION_POLL_MS = 60 * 1000;

// In-memory mirror of the localStorage cache + an in-flight Promise so
// concurrent callers in the same tab share one network request instead of
// each triggering their own (was the main driver of 44k calls).
let inMemoryCache: CachedOverrides | null = null;
let inFlight: Promise<Record<string, Record<string, Record<string, number>>>> | null = null;

interface WeightOverride {
  framework: string;
  mapping_key: string;
  weights: Record<string, number>;
  version: string;
}

interface CachedOverrides {
  timestamp: number;
  data: Record<string, Record<string, Record<string, number>>>;
  /** `tuning_weight_sets.version` in production when this was cached. */
  version?: number | null;
}

let lastVersionCheck = 0;

/**
 * In-memory-only override used by offline evaluation (e.g. rescoring a stored
 * simulation run with candidate weights). It never touches the database or
 * localStorage, so live scoring in other sessions is unaffected — the previous
 * approach of writing "temp_evaluation" rows made every user's scoring depend
 * on an admin's in-progress experiment.
 */
let ephemeralOverrides: Record<string, Record<string, Record<string, number>>> | null = null;

export function setEphemeralWeightOverrides(
  overrides: Record<string, Record<string, Record<string, number>>> | null,
): void {
  ephemeralOverrides = overrides;
}

export function hasEphemeralWeightOverrides(): boolean {
  return ephemeralOverrides !== null;
}

/**
 * Reads the version of the current production weight set. Promotion happens in
 * one admin's browser; every other session would otherwise keep serving the
 * previous weights until its 5-minute cache expired. Comparing this marker on
 * read makes the switch propagate on the next scoring call instead.
 */
async function readProductionWeightVersion(): Promise<number | null> {
  const { data, error } = await supabase
    .from('tuning_weight_sets')
    .select('version')
    .eq('status', 'production')
    .order('promoted_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return (data as { version: number | null }).version ?? null;
}

/** Fire-and-forget check: invalidates the local cache when production moved. */
function maybeCheckVersion(): void {
  if (Date.now() - lastVersionCheck < VERSION_POLL_MS) return;
  lastVersionCheck = Date.now();
  readProductionWeightVersion()
    .then((version) => {
      if (version === null) return;
      const known = inMemoryCache?.version ?? null;
      if (known !== null && known === version) return;
      if (known === null && inMemoryCache) {
        // Cache predates version tracking — stamp it rather than thrash.
        inMemoryCache = { ...inMemoryCache, version };
        try { localStorage.setItem(WEIGHT_CACHE_KEY, JSON.stringify(inMemoryCache)); } catch {}
        return;
      }
      clearWeightCache();
      loadWeightOverridesFromDB().catch(() => {});
    })
    .catch(() => {});
}

/**
 * Load weight overrides from database and cache them
 */
export async function loadWeightOverridesFromDB(): Promise<Record<string, Record<string, Record<string, number>>>> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
  try {
    const { data, error } = await supabase
      .from('scoring_weight_overrides')
      .select('framework, mapping_key, weights')
      .eq('is_active', true);

    if (error) {
      logger.warn('Failed to load weight overrides from DB', { 
        component: 'WeightLoader',
        metadata: { error: error.message }
      });
      return {};
    }

    const overrides: Record<string, Record<string, Record<string, number>>> = {};
    
    for (const row of data || []) {
      if (!overrides[row.framework]) {
        overrides[row.framework] = {};
      }
      overrides[row.framework][row.mapping_key] = normalizeWeights(
        row.weights as Record<string, number>
      );
    }

    // Cache in memory + localStorage
    const version = await readProductionWeightVersion().catch(() => null);
    lastVersionCheck = Date.now();
    const cached: CachedOverrides = { timestamp: Date.now(), data: overrides, version };
    inMemoryCache = cached;
    try { localStorage.setItem(WEIGHT_CACHE_KEY, JSON.stringify(cached)); } catch {}

    return overrides;
  } catch (e) {
    logger.warn('Error loading weight overrides', { 
      component: 'WeightLoader',
      metadata: { error: String(e) }
    });
    return {};
  }
  })();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

/**
 * Get cached weight overrides (synchronous, for use in mapping functions)
 */
export function getCachedWeightOverrides(): Record<string, Record<string, Record<string, number>>> | null {
  try {
    // Evaluation weights win while set, and deliberately skip cache refresh so
    // a background reload cannot swap weights mid-rescore.
    if (ephemeralOverrides) return ephemeralOverrides;
    if (inMemoryCache) {
      maybeCheckVersion();
      if (Date.now() - inMemoryCache.timestamp > CACHE_DURATION_MS && !inFlight) {
        loadWeightOverridesFromDB().catch(() => {});
      }
      return inMemoryCache.data;
    }
    const raw = localStorage.getItem(WEIGHT_CACHE_KEY);
    if (!raw) return null;

    const cached: CachedOverrides = JSON.parse(raw);
    inMemoryCache = cached;
    maybeCheckVersion();
    if (Date.now() - cached.timestamp > CACHE_DURATION_MS && !inFlight) {
      loadWeightOverridesFromDB().catch(() => {});
    }

    return cached.data;
  } catch {
    return null;
  }
}

/**
 * Get weight for a specific trait in a framework mapping
 * Returns the override if available, otherwise returns defaultWeight
 */
export function getWeight(
  framework: string,
  mappingKey: string,
  trait: string,
  defaultWeight: number
): number {
  const cached = getCachedWeightOverrides();
  return cached?.[framework]?.[mappingKey]?.[trait] ?? defaultWeight;
}

/**
 * Get all weights for a mapping key, merging with defaults
 */
export function getWeightsForMapping(
  framework: string,
  mappingKey: string,
  defaultWeights: Record<string, number>
): Record<string, number> {
  const cached = getCachedWeightOverrides();
  const overrides = cached?.[framework]?.[mappingKey] || {};
  
  return { ...defaultWeights, ...overrides };
}

/**
 * Get array weights for cognitive function mappings (MBTI style)
 * Returns override weights if available, otherwise defaults
 * 
 * Supports:
 * - Array format: [1.0, 1.2, 1.0, 0.8]
 * - Numeric keys: { "0": 1.0, "1": 1.2 }
 * - Trait names: { "Analytical": 1.5, "Independent": 1.3 } (requires traitNames param)
 */
export function getArrayWeights(
  framework: string,
  mappingKey: string,
  defaultWeights: number[],
  traitNames?: string[]  // Optional: for converting trait-keyed objects to arrays
): number[] {
  const cached = getCachedWeightOverrides();
  const overrides = cached?.[framework]?.[mappingKey];
  
  if (!overrides) return defaultWeights;
  
  // If already an array, return directly
  if (Array.isArray(overrides)) return overrides;
  
  // If overrides is object with numeric keys, convert to array
  const keys = Object.keys(overrides);
  if (keys.every(k => !isNaN(Number(k)))) {
    const sortedKeys = keys.sort((a, b) => Number(a) - Number(b));
    return sortedKeys.map(k => overrides[k] ?? defaultWeights[Number(k)] ?? 1);
  }
  
  // If traitNames provided and overrides uses trait names, map them to array positions
  if (traitNames && typeof overrides === 'object') {
    return traitNames.map((trait, i) => {
      // Check if this trait has an override
      if (trait in overrides) {
        return overrides[trait];
      }
      return defaultWeights[i] ?? 1.0;
    });
  }
  
  return defaultWeights;
}

/**
 * Clear the weight cache (call after applying new overrides)
 */
export function clearWeightCache(): void {
  inMemoryCache = null;
  lastVersionCheck = 0;
  localStorage.removeItem(WEIGHT_CACHE_KEY);
  try { localStorage.removeItem(WEIGHT_VERSION_KEY); } catch {}
}

/**
 * Force refresh weight cache from database
 */
export async function refreshWeightCache(): Promise<void> {
  clearWeightCache();
  await loadWeightOverridesFromDB();
}
